import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getSigningSessions } from 'app/entities/signing-session/signing-session.reducer';
import { SignatureEventType } from 'app/shared/model/enumerations/signature-event-type.model';
import { createEntity, getEntity, reset, updateEntity } from './signature-audit-log.reducer';

export const SignatureAuditLogUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const signingSessions = useAppSelector(state => state.signingSession.entities);
  const signatureAuditLogEntity = useAppSelector(state => state.signatureAuditLog.entity);
  const loading = useAppSelector(state => state.signatureAuditLog.loading);
  const updating = useAppSelector(state => state.signatureAuditLog.updating);
  const updateSuccess = useAppSelector(state => state.signatureAuditLog.updateSuccess);
  const signatureEventTypeValues = Object.keys(SignatureEventType);

  const handleClose = () => {
    navigate('/signature-audit-log');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getSigningSessions({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    values.eventTimestamp = convertDateTimeToServer(values.eventTimestamp);

    const entity = {
      ...signatureAuditLogEntity,
      ...values,
      signingSession: signingSessions.find(it => it.id.toString() === values.signingSession?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          eventTimestamp: displayDefaultDateTime(),
        }
      : {
          eventType: 'SESSION_CREATED',
          ...signatureAuditLogEntity,
          eventTimestamp: convertDateTimeFromServer(signatureAuditLogEntity.eventTimestamp),
          signingSession: signatureAuditLogEntity?.signingSession?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2
            id="crawlerBackendApp.signatureAuditLog.home.createOrEditLabel"
            data-cy="SignatureAuditLogCreateUpdateHeading"
          >
            <Translate contentKey="crawlerBackendApp.signatureAuditLog.home.createOrEditLabel">
              Create or edit a SignatureAuditLog
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="signature-audit-log-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.signatureAuditLog.eventType')}
                id="signature-audit-log-eventType"
                name="eventType"
                data-cy="eventType"
                type="select"
              >
                {signatureEventTypeValues.map(signatureEventType => (
                  <option value={signatureEventType} key={signatureEventType}>
                    {translate(`crawlerBackendApp.SignatureEventType.${signatureEventType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.signatureAuditLog.actor')}
                id="signature-audit-log-actor"
                name="actor"
                data-cy="actor"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.signatureAuditLog.ipAddress')}
                id="signature-audit-log-ipAddress"
                name="ipAddress"
                data-cy="ipAddress"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.signatureAuditLog.eventTimestamp')}
                id="signature-audit-log-eventTimestamp"
                name="eventTimestamp"
                data-cy="eventTimestamp"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.signatureAuditLog.note')}
                id="signature-audit-log-note"
                name="note"
                data-cy="note"
                type="text"
              />
              <ValidatedField
                id="signature-audit-log-signingSession"
                name="signingSession"
                data-cy="signingSession"
                label={translate('crawlerBackendApp.signatureAuditLog.signingSession')}
                type="select"
              >
                <option value="" key="0" />
                {signingSessions
                  ? signingSessions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/signature-audit-log"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default SignatureAuditLogUpdate;
